import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { ScrollSmoother } from 'gsap/ScrollSmoother';

gsap.registerPlugin(ScrollTrigger, ScrollSmoother);

/**
 * Hero Section - Animation des blobs (parallax souris + respiration)
 *
 * Chaque blob se déplace légèrement au mouvement de la souris (parallax)
 * et "respire" via une oscillation sinusoïdale pilotée par JS.
 * Le JS contrôle entièrement le transform pour éviter tout conflit avec CSS.
 */
const initHeroBlobAnimation = () => {
  const hero = document.querySelector('.hero-section');
  if (!hero) return;

  const blobs = hero.querySelectorAll('.hero-section__blob');
  if (!blobs.length) return;

  // Config par blob : depth parallax, amplitude respiration, vitesse respiration, décalage phase
  // Ordre : top-left-diffused, top-left, yellow-right, green-bottom-left
  const configs = [
    { depth: 0.012, breatheAmp: 0.07, breatheSpeed: 0.00065, phase: 0 },
    { depth: 0.022, breatheAmp: 0.055, breatheSpeed: 0.0009, phase: 1.8 },
    { depth: 0.018, breatheAmp: 0.065, breatheSpeed: 0.00075, phase: 3.4 },
    { depth: 0.015, breatheAmp: 0.05, breatheSpeed: 0.0006, phase: 5.1 },
  ];

  let targetX = 0;
  let targetY = 0;
  let currentX = 0;
  let currentY = 0;

  // Position souris relative au centre de la section
  hero.addEventListener('mousemove', (event) => {
    const rect = hero.getBoundingClientRect();
    targetX = event.clientX - rect.left - rect.width / 2;
    targetY = event.clientY - rect.top - rect.height / 2;
  });

  // Retour au centre quand la souris quitte la section
  hero.addEventListener('mouseleave', () => {
    targetX = 0;
    targetY = 0;
  });

  let startTime = null;

  const animate = (timestamp) => {
    if (!startTime) startTime = timestamp;
    const elapsed = timestamp - startTime;

    // Lissage de la position souris
    currentX += (targetX - currentX) * 0.055;
    currentY += (targetY - currentY) * 0.055;

    blobs.forEach((blob, index) => {
      const config = configs[index] ?? configs[0];

      const parallaxX = currentX * config.depth;
      const parallaxY = currentY * config.depth;

      // Respiration via oscillation sinusoïdale
      const scale = 1 + Math.sin(elapsed * config.breatheSpeed + config.phase) * config.breatheAmp;

      blob.style.transform = `translate(${parallaxX}px, ${parallaxY}px) scale(${scale})`;
    });

    requestAnimationFrame(animate);
  };

  requestAnimationFrame(animate);
};

/**
 * Menu mobile — bulle de navigation animée avec GSAP
 *
 * Ouverture  : backdrop fade-in + bulle pop-in (scale 0→1 depuis coin supérieur droit) + liens en stagger
 * Fermeture  : liens fade-out + bulle pop-out (scale 1→0 vers coin supérieur droit) + backdrop fade-out
 * Accessibilité : aria-expanded, aria-hidden, Échap, clic backdrop, clic lien
 */
const initHeroMenuToggle = () => {
  const heroToggle     = document.querySelector('.hero-section__menu-toggle');
  const floatingToggle = document.querySelector('.floating-menu-toggle');
  const modal          = document.querySelector('.hero-section__navigation-modal');
  const backdrop       = document.querySelector('.hero-section__navigation-backdrop');
  if (!modal) return;

  const allToggles = [heroToggle, floatingToggle].filter(Boolean);
  if (!allToggles.length) return;

  const links = Array.from(modal.querySelectorAll('.hero-section__navigation-modal-list a'));

  // Bouton flottant : état initial invisible (la visibilité est gérée par initScrollSnap)
  if (floatingToggle) gsap.set(floatingToggle, { autoAlpha: 0 });

  // État initial — bulle invisible (scale 0), backdrop invisible
  gsap.set(modal, { scale: 0, autoAlpha: 0, transformOrigin: 'top right' });
  gsap.set(backdrop, { autoAlpha: 0 });
  gsap.set(links, { autoAlpha: 0, y: 8 });

  let isOpen = false;

  const openMenu = () => {
    isOpen = true;
    allToggles.forEach((t) => t.setAttribute('aria-expanded', 'true'));
    modal.setAttribute('aria-hidden', 'false');
    document.body.classList.add('navigation-modal-open');

    // Bulle — pop-in depuis le coin supérieur droit (bouton)
    gsap.to(modal, { scale: 1, autoAlpha: 1, duration: 0.4, ease: 'back.out(1.4)' });
    // Backdrop
    gsap.to(backdrop, { autoAlpha: 1, duration: 0.3, ease: 'none' });
    // Liens — apparaissent en stagger depuis le bas
    gsap.to(links, {
      autoAlpha: 1,
      y: 0,
      duration: 0.35,
      stagger: 0.06,
      ease: 'power2.out',
      delay: 0.2,
    });
  };

  const closeMenu = () => {
    isOpen = false;
    allToggles.forEach((t) => t.setAttribute('aria-expanded', 'false'));
    modal.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('navigation-modal-open');

    // Liens — disparaissent rapidement
    gsap.to(links, { autoAlpha: 0, y: 8, duration: 0.15, stagger: 0.03, ease: 'power2.in' });
    // Bulle — se referme vers le coin supérieur droit
    gsap.to(modal, { scale: 0, autoAlpha: 0, duration: 0.25, ease: 'power2.in', delay: 0.1 });
    // Backdrop
    gsap.to(backdrop, { autoAlpha: 0, duration: 0.25, ease: 'none', delay: 0.05 });
  };

  allToggles.forEach((toggle) => {
    toggle.addEventListener('click', () => (isOpen ? closeMenu() : openMenu()));
  });

  // Clic backdrop → ferme
  backdrop.addEventListener('click', closeMenu);

  // Clic sur un lien → ferme (navigation one-page)
  links.forEach((link) => link.addEventListener('click', closeMenu));

  // Touche Échap → ferme
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && isOpen) closeMenu();
  });

  return { closeMenu };
};

/**
 * Hero — animations d'entrée
 *
 * Logo → tagline → CTA → toggle (pop-in en dernier).
 * Démarre 0.2 s après le chargement.
 * Retourne la timeline pour qu'elle puisse être inversée (scroll vers bas)
 * ou redémarrée (retour sur le hero).
 */
const initHeroEntrance = () => {
  const hero = document.querySelector('.hero-section');
  if (!hero) return null;

  const logo        = hero.querySelector('.hero-section__logo');
  const tagline     = hero.querySelector('.hero-section__tagline');
  const buttonGroup = hero.querySelector('.hero-section__button-group');
  const toggle      = hero.querySelector('.hero-section__menu-toggle');

  if (!logo) return null;

  gsap.set(logo, { autoAlpha: 0, y: 32 });
  if (tagline)     gsap.set(tagline,     { autoAlpha: 0, y: 24 });
  if (buttonGroup) gsap.set(buttonGroup, { autoAlpha: 0, y: 20 });
  if (toggle)      gsap.set(toggle,      { autoAlpha: 0, scale: 0.75 });

  const tl = gsap.timeline({ paused: true });
  tl.to(logo, { autoAlpha: 1, y: 0, duration: 0.75, ease: 'power2.out' });
  if (tagline)     tl.to(tagline,     { autoAlpha: 1, y: 0, duration: 0.65, ease: 'power2.out' }, 0.3);
  if (buttonGroup) tl.to(buttonGroup, { autoAlpha: 1, y: 0, duration: 0.55, ease: 'power2.out' }, 0.55);
  if (toggle)      tl.to(toggle,      { autoAlpha: 1, scale: 1, duration: 0.45, ease: 'back.out(1.4)' }, 0.85);

  // Disparition rapide — joue la timeline en reverse rapide (timeScale ×3 ≈ 0.43s)
  tl.fadeOut = () => {
    tl.timeScale(3).reverse();
  };

  tl.playReturn = () => tl.timeScale(1).restart();

  gsap.delayedCall(0.2, () => tl.play());

  return tl;
};

/**
 * ScrollSmoother + Parallax Hero
 *
 * ScrollSmoother ajoute une inertie globale au scroll de la page.
 *
 * Requis par ScrollSmoother :
 *  - #smooth-wrapper → overflow: hidden; position: fixed (voir _base.scss)
 *  - #smooth-content → translateY animé par ScrollSmoother
 *
 * Le fondu bas du hero est géré par initHeroMask() via ScrollTrigger.
 */
const initScrollSmoother = () => {
  const wrapper = document.querySelector('#smooth-wrapper');
  const content = document.querySelector('#smooth-content');
  if (!wrapper || !content) return null;

  const smoother = ScrollSmoother.create({
    wrapper: '#smooth-wrapper',
    content: '#smooth-content',
    // Inertie du scroll : 1 = léger, 2 = prononcé
    smooth: 1.5,
  });

  return smoother;
};

/**
 * Scroll snap — navigation automatique entre sections
 *
 * Intercepte wheel, touch et clavier pour naviguer de section en section.
 * Le scroll est verrouillé pendant la transition → l'utilisateur ne peut pas
 * scroller librement entre deux sections.
 *
 * smoother.offset() retourne la position de scroll exacte pour placer
 * le haut de la section cible en haut du viewport.
 */
const initScrollSnap = (smoother, heroTl = null, closeMenu = null) => {
  if (!smoother) return;
  if (window.matchMedia('(max-width: 1023px)').matches) return;

  const sections = [
    document.querySelector('.hero-section'),
    document.querySelector('.program-partner-presentation'),
  ].filter(Boolean);

  if (sections.length < 2) return;

  const floatingToggle = document.querySelector('.floating-menu-toggle');

  // Blobs décoratifs — fade out au départ, fade in au retour
  const heroBlobs = Array.from(sections[0].querySelectorAll('.hero-section__blob'));
  const fadeHeroBlobs = (out, delay = 0) => {
    if (!heroBlobs.length) return;
    gsap.to(heroBlobs, {
      opacity: out ? 0 : 1,
      duration: out ? 0.4 : 0.7,
      delay,
      ease: out ? 'power2.in' : 'power2.out',
      overwrite: 'auto',
    });
  };

  // Couleur de fond originale du hero (#313B82 = $blue)
  const HERO_BG_ORIGINAL = '#313B82';
  const animateHeroBg = (toWhite, duration, delay = 0) => {
    gsap.to(sections[0], {
      backgroundColor: toWhite ? '#ffffff' : HERO_BG_ORIGINAL,
      duration,
      delay,
      ease: 'power2.inOut',
      overwrite: 'auto',
    });
  };

  // Éléments du container de la section présentation à animer à l'entrée
  const presentationItems = Array.from(
    document.querySelectorAll('.program-partner-presentation__container > *'),
  );

  // Décorations SVG du titre — apparaissent après l'animation d'entrée
  const titleDecorations = Array.from(
    document.querySelectorAll('.program-partner-presentation__title-decoration'),
  );

  // État initial : invisible et décalé vers le bas
  gsap.set(presentationItems, { opacity: 0, y: 40 });
  // SVG masqués dès le départ, même quand le h2 parent devient visible
  if (titleDecorations.length) gsap.set(titleDecorations, { autoAlpha: 0, scale: 0.5, transformOrigin: 'center center' });

  // Entrée en fondu depuis le bas, en cascade — se joue une seule fois
  let presentationAnimated = false;

  const showFloatingToggle = () => {
    if (floatingToggle) gsap.to(floatingToggle, { autoAlpha: 1, duration: 0.3, ease: 'power2.out' });
  };

  const hideFloatingToggle = () => {
    if (floatingToggle) gsap.to(floatingToggle, { autoAlpha: 0, duration: 0.2 });
  };

  const animatePresentationIn = () => {
    if (presentationAnimated) return;
    presentationAnimated = true;

    gsap.to(presentationItems, {
      opacity: 1,
      y: 0,
      duration: 0.7,
      stagger: 0.12,
      ease: 'power2.out',
      onComplete: () => {
        showFloatingToggle();
        // Apparition des décorations SVG en pop après l'animation d'entrée
        if (titleDecorations.length) {
          gsap.to(titleDecorations, {
            autoAlpha: 1,
            scale: 1,
            duration: 0.4,
            ease: 'back.out(1.7)',
            stagger: 0.08,
          });
        }
      },
    });
  };

  // Hero : inverser l'animation d'entrée quand on quitte, relancer quand on revient
  ScrollTrigger.create({
    trigger: sections[0],
    start: 'top top',
    onLeave: () => {
      if (heroTl) heroTl.fadeOut();
      animateHeroBg(true, 0.6);
      fadeHeroBlobs(true);
    },
    onEnterBack: () => {
      // Guarded : pendant un snap isAnimating est true, on laisse goTo gérer le restart
      if (!isAnimating && heroTl) heroTl.playReturn();
      if (!isAnimating) animateHeroBg(false, 0.6);
      if (!isAnimating) fadeHeroBlobs(false);
    },
  });

  // Scrollbar / scroll libre : déclencher animation + bouton quand section pleinement visible
  ScrollTrigger.create({
    trigger: sections[1],
    start: 'top top',
    onEnter: () => {
      if (!presentationAnimated) animatePresentationIn();
      else showFloatingToggle();
    },
    onEnterBack: showFloatingToggle,
  });

  // Cacher le bouton quand la section commence à remonter (avant d'arriver sur le hero)
  ScrollTrigger.create({
    trigger: sections[1],
    start: 'top 40%',
    onLeaveBack: hideFloatingToggle,
  });

  let isAnimating = false;

  const goTo = (index) => {
    if (isAnimating || index < 0 || index >= sections.length) return;
    isAnimating = true;

    // Fermer la modale si elle est ouverte
    closeMenu?.();

    // Retour au hero → cacher le bouton + éléments hero immédiatement invisibles
    if (index === 0) {
      hideFloatingToggle();
      if (heroTl) heroTl.pause(0); // éléments à opacity 0 pendant le snap
      // Délai de 0.4s : le hero entre dans le viewport vers t=0.4s (power2.inOut)
      // → fond + blobs démarrent quand le hero est visible, avant l'animation d'entrée
      animateHeroBg(false, 0.55, 0.4);
      fadeHeroBlobs(false, 0.4); // blobs en premier (0.7s) → finis à ~t=1.1s
    }
    // Départ du hero → fade-out rapide + fond vers le blanc + blobs
    if (index === 1) {
      if (heroTl) heroTl.fadeOut();
      animateHeroBg(true, 0.65);
      fadeHeroBlobs(true);
    }

    gsap.to(smoother, {
      scrollTop: smoother.offset(sections[index], 'top top'),
      duration: 1,
      ease: 'power2.inOut',
      onComplete: () => {
        isAnimating = false;
        // Retour sur le hero → blobs déjà en cours, éléments après 0.15s
        if (index === 0 && heroTl) gsap.delayedCall(0.15, () => heroTl.playReturn());
        if (index === 1) {
          if (!presentationAnimated) {
            // Première visite : animation + affichage bouton en onComplete
            animatePresentationIn();
          } else {
            // Retour sur la section déjà animée : afficher le bouton directement
            showFloatingToggle();
          }
        }
      },
    });
  };

  /**
   * Retourne l'index de la section de snap la plus proche de la position actuelle,
   * ou -1 si on est au-delà de la dernière section (zone hors snap).
   *
   * Logique : toute position entre section 0 et section N-1 (+80px de tolérance)
   * est considérée dans la zone de snap → évite les ratés lors d'un scroll rapide
   * où l'inertie de ScrollSmoother dépasse la tolérance fixe de 80px.
   */
  const getSnapIndex = () => {
    const scrollTop = smoother.scrollTop();
    const offsets   = sections.map(s => smoother.offset(s, 'top top'));

    // Au-delà de la dernière section : hors zone de snap
    if (scrollTop > offsets[offsets.length - 1] + 80) return -1;

    // Dans la zone : section la plus proche
    return offsets.reduce(
      (closest, offset, i) =>
        Math.abs(scrollTop - offset) < Math.abs(scrollTop - offsets[closest]) ? i : closest,
      0,
    );
  };

  // Molette — on n'intercepte que si on est physiquement dans la zone de snap.
  // Si l'utilisateur a scrollé librement au-delà, getSnapIndex() retourne -1
  // et les événements passent normalement → plus de remontée intempestive.
  window.addEventListener('wheel', (e) => {
    if (isAnimating) {
      e.preventDefault();
      return;
    }
    const snapIndex = getSnapIndex();
    if (snapIndex === -1) return;

    const goDown = e.deltaY > 0 && snapIndex < sections.length - 1;
    const goUp   = e.deltaY < 0 && snapIndex > 0;
    if (goDown || goUp) {
      e.preventDefault();
      if (goDown) goTo(snapIndex + 1);
      else goTo(snapIndex - 1);
    }
  }, { passive: false });

  // Touch (mobile)
  let touchStartY = 0;
  window.addEventListener('touchstart', (e) => {
    touchStartY = e.touches[0].clientY;
  }, { passive: true });

  window.addEventListener('touchend', (e) => {
    if (isAnimating) return;
    const delta = touchStartY - e.changedTouches[0].clientY;
    if (Math.abs(delta) < 50) return;
    const snapIndex = getSnapIndex();
    if (snapIndex === -1) return;
    if (delta > 0 && snapIndex < sections.length - 1) goTo(snapIndex + 1);
    else if (delta < 0 && snapIndex > 0) goTo(snapIndex - 1);
  }, { passive: true });

  // Clavier
  window.addEventListener('keydown', (e) => {
    if (isAnimating) return;
    const snapIndex = getSnapIndex();
    if (snapIndex === -1) return;
    if ((e.key === 'ArrowDown' || e.key === 'PageDown') && snapIndex < sections.length - 1) {
      e.preventDefault();
      goTo(snapIndex + 1);
    } else if ((e.key === 'ArrowUp' || e.key === 'PageUp') && snapIndex > 0) {
      e.preventDefault();
      goTo(snapIndex - 1);
    }
  });

  // Exposé pour la navigation par ancres
  const navigateTo = (el) => {
    const snapIndex = sections.indexOf(el);
    if (snapIndex !== -1) {
      // Section de snap : goTo déclenche aussi les animations d'entrée
      goTo(snapIndex);
    } else {
      // Section hors snap : scroll direct via offset (tient compte des pins)
      gsap.to(smoother, {
        scrollTop: smoother.offset(el, 'top top'),
        duration: 0.9,
        ease: 'power2.inOut',
      });
    }
  };

  return { navigateTo };
};

/**
 * Fondu bas du hero — lié au scroll (scrub)
 *
 * Le masque est absent au chargement (hero intact).
 * Dès que le hero commence à quitter le viewport, le gradient grandit
 * vers le haut progressivement en sync avec le scroll.
 *
 * scrub: true → chaque frame met à jour les stops du gradient
 * en proportion exacte du scroll, sans saut ni décalage.
 */
const initHeroMask = () => {
  const hero = document.querySelector('.hero-section');
  if (!hero) return;

  ScrollTrigger.create({
    trigger: hero,
    start: 'top top',
    end: 'bottom top',
    scrub: true,
    onUpdate(self) {
      const p = self.progress;

      if (p <= 0) {
        hero.style.removeProperty('-webkit-mask-image');
        hero.style.removeProperty('mask-image');
        return;
      }

      // Courbe puissance (√p) : le masque monte rapidement dès le début du scroll
      // puis ralentit — bien plus visible qu'une progression linéaire.
      // p=0.05 → masque à ~78% (vs 95% en linéaire), p=0.1 → ~68% (vs 90%)
      const opaqueEnd = Math.max(0, (1 - Math.pow(p, 0.5)) * 100);
      const mask = `linear-gradient(to bottom, black ${opaqueEnd}%, transparent 100%)`;
      hero.style.setProperty('-webkit-mask-image', mask);
      hero.style.setProperty('mask-image', mask);
    },
  });
};

/**
 * Concrete Benefits — révélation séquentielle des statistiques au scroll
 *
 * La section est épinglée (pin) pendant que les stats apparaissent une par une.
 * Le titre et sous-titre restent toujours visibles.
 *
 * Une GSAP timeline est liée (scrub) au ScrollTrigger :
 *   - Chaque stat fade-in depuis le bas (autoAlpha 0→1, y 40→0)
 *   - Une pause dans la timeline crée une zone de "lecture" sans animation
 *   - La section se déépingle automatiquement une fois toutes les stats visibles
 */
const initConcreteBenefits = () => {
  const section = document.querySelector('.concrete-benefits');
  if (!section) return;

  // Animation d'entrée du sous-titre — se joue avant le pin
  const subtitle = section.querySelector('.concrete-benefits__subtitle');
  if (subtitle) {
    gsap.set(subtitle, { opacity: 0, y: 30 });
    const subtitleObserver = new IntersectionObserver(
      (entries) => {
        if (!entries[0].isIntersecting) return;
        subtitleObserver.unobserve(subtitle);
        gsap.to(subtitle, { opacity: 1, y: 0, duration: 0.7, ease: 'power2.out' });
      },
      { rootMargin: '0px 0px -20% 0px' },
    );
    subtitleObserver.observe(subtitle);
  }

  const items = gsap.utils.toArray('.concrete-benefits__stat-item');
  if (!items.length) return;

  // Mobile : IntersectionObserver simple, pas de pin (le pin GSAP est incompatible
  // avec le viewport dynamique des navigateurs mobiles et le scroll inertiel)
  const isDesktop = window.matchMedia('(min-width: 1024px)').matches;

  if (!isDesktop) {
    items.forEach((item, i) => {
      gsap.set(item, { autoAlpha: 0, y: 40 });
      const observer = new IntersectionObserver(
        (entries) => {
          if (!entries[0].isIntersecting) return;
          observer.unobserve(item);
          gsap.to(item, {
            autoAlpha: 1,
            y: 0,
            duration: 0.7,
            delay: i * 0.1,
            ease: 'power2.out',
          });
        },
        { rootMargin: '0px 0px -10% 0px' },
      );
      observer.observe(item);
    });
    return;
  }

  // Desktop : Timeline épinglée pilotée par le scroll
  // État initial : tous les items invisibles et décalés vers le bas
  gsap.set(items, { autoAlpha: 0, y: 50 });

  // Timeline : chaque item entre en fondu, suivi d'une pause de lecture
  const tl = gsap.timeline();

  items.forEach((item) => {
    tl.to(item, {
      autoAlpha: 1,
      y: 0,
      duration: 1,
      ease: 'power2.out',
    }).to({}, { duration: 0.4 }); // zone de pause entre deux stats
  });

  // Pin de la section + animation pilotée par le scroll
  // end = 0.7 × innerHeight par stat
  ScrollTrigger.create({
    trigger: section,
    start: 'top top',
    end: `+=${items.length * Math.round(window.innerHeight * 0.7)}`,
    pin: true,
    scrub: 0.8,
    animation: tl,
  });
};

/**
 * Platform Showcase — hotspots interactifs sur le mockup navigateur
 *
 * Desktop (pointer fine) : survol → tooltip visible, quitter → caché.
 * Mobile / touch (pointer coarse) : clic toggle ; clic extérieur → ferme tout.
 *
 * Le tooltip se repositionne en dessous du bouton si l'espace au-dessus
 * est insuffisant (moins de 150px depuis le haut du viewport).
 */
const initPlatformShowcase = () => {
  const section = document.querySelector('.platform-showcase');

  // --- Animations d'entrée ---
  if (section) {
    const subtitle     = section.querySelector('.platform-showcase__subtitle');
    const featureItems = section.querySelectorAll('.platform-showcase__feature-item');
    const browserFrame = section.querySelector('.platform-showcase__browser-frame');

    const observe = (el, animFn, margin = '0px 0px -20% 0px') => {
      const obs = new IntersectionObserver(
        (entries) => {
          if (!entries[0].isIntersecting) return;
          obs.unobserve(entries[0].target);
          animFn();
        },
        { rootMargin: margin },
      );
      obs.observe(el);
    };

    if (subtitle) {
      gsap.set(subtitle, { opacity: 0, y: 25 });
      observe(subtitle, () =>
        gsap.to(subtitle, { opacity: 1, y: 0, duration: 0.7, ease: 'power2.out' }),
      );
    }

    if (featureItems.length) {
      gsap.set(featureItems, { opacity: 0, y: 20 });
      observe(featureItems[0], () =>
        gsap.to(featureItems, {
          opacity: 1,
          y: 0,
          duration: 0.55,
          stagger: 0.1,
          ease: 'power2.out',
        }),
      );
    }

    if (browserFrame) {
      gsap.set(browserFrame, { opacity: 0, y: 50, scale: 0.98 });
      observe(
        browserFrame,
        () => gsap.to(browserFrame, { opacity: 1, y: 0, scale: 1, duration: 0.9, ease: 'power3.out' }),
        '0px 0px -10% 0px',
      );
    }


    const isDesktop = window.matchMedia('(min-width: 1024px)').matches;

    // Animation pop-in des pins numérotés — sur toutes les tailles
    const hotspotButtons = section.querySelectorAll('.platform-showcase__hotspot-button');
    if (hotspotButtons.length) {
      gsap.set(hotspotButtons, { opacity: 0, scale: 0.6 });
      observe(
        hotspotButtons[0],
        () =>
          gsap.to(hotspotButtons, {
            stagger: 0.12,
            keyframes: [
              { opacity: 1, scale: 1.35, duration: 0.35, ease: 'power3.out' },
              { scale: 1,   duration: 0.25, ease: 'power2.inOut' },
            ],
          }),
        '0px 0px -30% 0px',
      );
    }

    // Mobile : animation fade-in des items de la liste hotspot
    if (!isDesktop) {
      const mobileItems = section.querySelectorAll('.platform-showcase__hotspot-mobile-item');
      mobileItems.forEach((item, i) => {
        gsap.set(item, { opacity: 0, x: -20 });
        observe(
          item,
          () => gsap.to(item, { opacity: 1, x: 0, duration: 0.5, delay: i * 0.08, ease: 'power2.out' }),
          '0px 0px -5% 0px',
        );
      });
    }
  }

  // --- Hotspots interactifs (desktop uniquement) ---
  const isDesktopInteraction = window.matchMedia('(min-width: 1024px)').matches;
  const buttons = isDesktopInteraction
    ? Array.from(document.querySelectorAll('.platform-showcase__hotspot-button'))
    : [];
  if (!buttons.length) return;

  const isTouch = () => window.matchMedia('(pointer: coarse)').matches;

  const showTooltip = (btn) => {
    const tooltip = btn.querySelector('.platform-showcase__hotspot-tooltip');
    if (!tooltip) return;
    // Repositionner en dessous si le hotspot est trop proche du haut de l'image
    const screen = btn.closest('.platform-showcase__browser-screen');
    const btnRect = btn.getBoundingClientRect();
    const screenRect = screen ? screen.getBoundingClientRect() : { top: 0 };
    const spaceAboveInImage = btnRect.top - screenRect.top;
    tooltip.classList.toggle(
      'platform-showcase__hotspot-tooltip--below',
      spaceAboveInImage < 120,
    );
    tooltip.classList.add('platform-showcase__hotspot-tooltip--visible');
    btn.classList.add('platform-showcase__hotspot-button--active');
    btn.setAttribute('aria-expanded', 'true');
  };

  const hideTooltip = (btn) => {
    const tooltip = btn.querySelector('.platform-showcase__hotspot-tooltip');
    if (!tooltip) return;
    tooltip.classList.remove('platform-showcase__hotspot-tooltip--visible');
    // --below est conservé pendant le fondu sortant pour éviter un saut de position.
    // Il sera recalculé par showTooltip à la prochaine ouverture.
    btn.classList.remove('platform-showcase__hotspot-button--active');
    btn.setAttribute('aria-expanded', 'false');
  };

  const hideAll = () => buttons.forEach(hideTooltip);

  buttons.forEach((btn) => {
    // Desktop : hover
    btn.addEventListener('mouseenter', () => {
      if (!isTouch()) showTooltip(btn);
    });
    btn.addEventListener('mouseleave', () => {
      if (!isTouch()) hideTooltip(btn);
    });

    // Mobile + fallback click : toggle
    btn.addEventListener('click', (e) => {
      e.stopPropagation();
      const isActive = btn.classList.contains(
        'platform-showcase__hotspot-button--active',
      );
      hideAll();
      if (!isActive) showTooltip(btn);
    });
  });

  // Clic en dehors → fermer tous
  document.addEventListener('click', hideAll);

  // Touche Échap → fermer tous
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') hideAll();
  });
};

// -----------------------------------------------
// Partners Showcase — carousel (mobile/tablet) + entrée GSAP (desktop)
// -----------------------------------------------
const initPartnersShowcase = () => {
  const section = document.querySelector('.partners-showcase');
  const logos = document.querySelectorAll('.partners-showcase__logo-item');
  if (!section || !logos.length) return;

  const isDesktop = () => window.matchMedia('(min-width: 1024px)').matches;

  // --- Mobile / Tablet : carousel infini ---
  const initCarousel = () => {
    const track = section.querySelector('.partners-showcase__logo-track');
    if (!track) return;

    // Cloner chaque logo-item dans le même track pour la boucle sans coupure
    // (un seul track, deux séries de logos → animation translateX(-50%) = 1 tour)
    const items = Array.from(track.querySelectorAll('.partners-showcase__logo-item'));
    items.forEach((item) => {
      const clone = item.cloneNode(true);
      clone.setAttribute('aria-hidden', 'true');
      track.appendChild(clone);
    });

    // Déclencher l'animation CSS sur le track unique
    track.classList.add('partners-showcase__logo-track--scrolling');
  };

  // --- Desktop : révélation au scroll logo par logo ---
  // IntersectionObserver utilise la position visuelle → compatible avec ScrollSmoother
  const initDesktopEntrance = () => {
    gsap.set(logos, { opacity: 0, y: 18, scale: 0.88 });

    const visibilityMap = new Map(Array.from(logos).map((l) => [l, false]));

    const hideLogo = (logo) => {
      if (!visibilityMap.get(logo)) return;
      visibilityMap.set(logo, false);
      gsap.to(logo, {
        opacity: 0,
        y: 18,
        scale: 0.88,
        duration: 0.5,
        ease: 'power2.in',
        overwrite: true,
      });
    };

    // Logos "bas" (nth-child 7, 10, 11, 13, 14, 15, 16 → index 0-based)
    const bottomLogoIndices = new Set([6, 9, 10, 12, 13, 14, 15]);

    const showLogo = (logo) => {
      if (visibilityMap.get(logo)) return;
      visibilityMap.set(logo, true);
      gsap.to(logo, { opacity: 1, y: 0, scale: 1, duration: 0.65, ease: 'power2.out', overwrite: true });
    };

    // Logos haut : apparaissent à 70% du viewport → marge négative de 30%
    // Logos bas  : apparaissent à 90% du viewport → marge négative de 10%
    // Pas de marge positive : évite de déclencher avant que le logo soit visible
    const showObserverTop = new IntersectionObserver(
      (entries) => entries.forEach((e) => e.isIntersecting && showLogo(e.target)),
      { rootMargin: '0px 0px -30% 0px' },
    );

    const showObserverBottom = new IntersectionObserver(
      (entries) => entries.forEach((e) => e.isIntersecting && showLogo(e.target)),
      { rootMargin: '0px 0px -10% 0px' },
    );

    // Disparition individuelle au scroll vers le haut (inchangée)
    const hideObserver = new IntersectionObserver(
      (entries) => entries.forEach((e) => !e.isIntersecting && hideLogo(e.target)),
      { rootMargin: '0px 0px -10% 0px' },
    );

    Array.from(logos).forEach((logo, i) => {
      const showObserver = bottomLogoIndices.has(i) ? showObserverBottom : showObserverTop;
      showObserver.observe(logo);
      hideObserver.observe(logo);
    });
  };

  if (isDesktop()) {
    initDesktopEntrance();
  } else {
    initCarousel();
  }
};

/**
 * Key Benefits — Animations d'entrée GSAP
 *
 * Les items de la grille apparaissent en fondu + glissement vers le haut en stagger.
 * Déclenchement à 80% du viewport (rootMargin -20%).
 */
const initKeyBenefits = () => {
  const items = document.querySelectorAll('.key-benefits__item');
  if (!items.length) return;

  gsap.set(items, { opacity: 0, y: 25 });

  const obs = new IntersectionObserver(
    (entries) => {
      if (!entries[0].isIntersecting) return;
      obs.unobserve(entries[0].target);
      gsap.to(items, {
        opacity: 1,
        y: 0,
        duration: 0.6,
        stagger: 0.1,
        ease: 'power2.out',
      });
    },
    { rootMargin: '0px 0px -20% 0px' },
  );

  obs.observe(items[0]);
};

/**
 * Pricing Model — Animations d'entrée GSAP
 *
 * Éléments animés (hors titre géré par initTitleAnimations) :
 * - subscription-card et value-column : fondu + glissement vers le haut
 * - features-title : fondu + glissement
 * - feature-items : fondu + glissement en stagger
 * Déclenchement à 80% du viewport (rootMargin -20%).
 */
const initPricingModel = () => {
  const section = document.querySelector('.pricing-model');
  if (!section) return;

  const observe = (el, animFn, margin = '0px 0px -20% 0px') => {
    const obs = new IntersectionObserver(
      (entries) => {
        if (!entries[0].isIntersecting) return;
        obs.unobserve(entries[0].target);
        animFn();
      },
      { rootMargin: margin },
    );
    obs.observe(el);
  };

  const subscriptionCard = section.querySelector('.pricing-model__subscription-card');
  const valueColumn      = section.querySelector('.pricing-model__value-column');
  const featuresTitle    = section.querySelector('.pricing-model__features-title');
  const featureItems     = section.querySelectorAll('.pricing-model__feature-item');

  if (subscriptionCard) {
    gsap.set(subscriptionCard, { opacity: 0, y: 30 });
    observe(subscriptionCard, () =>
      gsap.to(subscriptionCard, { opacity: 1, y: 0, duration: 0.7, ease: 'power2.out' }),
    );
  }

  if (valueColumn) {
    gsap.set(valueColumn, { opacity: 0, y: 30 });
    observe(valueColumn, () =>
      gsap.to(valueColumn, { opacity: 1, y: 0, duration: 0.7, delay: 0.15, ease: 'power2.out' }),
    );
  }

  if (featuresTitle) {
    gsap.set(featuresTitle, { opacity: 0, y: 25 });
    observe(featuresTitle, () =>
      gsap.to(featuresTitle, { opacity: 1, y: 0, duration: 0.7, ease: 'power2.out' }),
    );
  }

  if (featureItems.length) {
    gsap.set(featureItems, { opacity: 0, y: 20 });
    observe(featureItems[0], () =>
      gsap.to(featureItems, {
        opacity: 1,
        y: 0,
        duration: 0.5,
        stagger: 0.07,
        ease: 'power2.out',
      }),
    );
  }
};

/**
 * FAQ — Accordéon accessible
 *
 * Clic sur le bouton : toggle aria-expanded + hidden sur la réponse.
 * Une seule réponse ouverte à la fois.
 */
const initFaq = () => {
  const items = Array.from(document.querySelectorAll('.faq-section__item'));
  if (!items.length) return;

  items.forEach((item) => {
    const button = item.querySelector('.faq-section__question-button');
    if (!button) return;

    button.addEventListener('click', () => {
      const isOpen = button.getAttribute('aria-expanded') === 'true';

      // Fermer tous les autres items
      items.forEach((other) => {
        if (other === item) return;
        const otherButton = other.querySelector('.faq-section__question-button');
        if (!otherButton) return;
        otherButton.setAttribute('aria-expanded', 'false');
        other.classList.remove('faq-section__item--open');
      });

      // Toggle l'item courant
      button.setAttribute('aria-expanded', String(!isOpen));
      item.classList.toggle('faq-section__item--open', !isOpen);
    });
  });
};

/**
 * Solution Overview — animation d'entrée en fondu
 *
 * Titre, intro, feature cards et benefit cards entrent en cascade
 * depuis le bas au premier scroll dans la section.
 * Utilise IntersectionObserver (position visuelle) pour être compatible
 * avec ScrollSmoother.
 */
const initSolutionOverview = () => {
  const section = document.querySelector('.solution-overview');
  if (!section) return;

  const elements = [
    section.querySelector('.solution-overview__main-title'),
    section.querySelector('.solution-overview__intro-paragraph'),
    section.querySelector('.solution-overview__features-label'),
    ...section.querySelectorAll('.solution-overview__feature-card'),
    section.querySelector('.solution-overview__benefits-intro'),
    ...section.querySelectorAll('.solution-overview__benefit-card'),
  ].filter(Boolean);

  if (!elements.length) return;

  gsap.set(elements, { opacity: 0, y: 40 });

  // Chaque élément se déclenche individuellement quand il atteint 70% du viewport
  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        observer.unobserve(entry.target);
        gsap.to(entry.target, {
          opacity: 1,
          y: 0,
          duration: 0.7,
          ease: 'power2.out',
        });
      });
    },
    { rootMargin: '0px 0px -20% 0px' },
  );

  elements.forEach((el) => observer.observe(el));
};

/**
 * Animations des titres principaux — apparition en fondu au scroll
 *
 * Se joue une seule fois à l'entrée dans le viewport (80%).
 */
const initTitleAnimations = () => {
  const titles = document.querySelectorAll([
    '.solution-overview__main-title',
    '.concrete-benefits__title',
    '.platform-showcase__title',
    '.pricing-model__section-title',
    '.partners-showcase__title',
  ].join(', '));

  if (!titles.length) return;

  gsap.set(titles, { opacity: 0 });

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        observer.unobserve(entry.target);
        gsap.to(entry.target, { opacity: 1, duration: 0.7, ease: 'power2.out' });
      });
    },
    { rootMargin: '0px 0px -20% 0px' },
  );

  titles.forEach((title) => observer.observe(title));
};

/**
 * Navigation par ancres — scroll fluide via ScrollSmoother
 *
 * Utilise navigateTo() de initScrollSnap (desktop) pour respecter les pins
 * et déclencher les animations d'entrée des sections de snap.
 * Fallback mobile : gsap.to avec smoother.offset() pour tenir compte des pins.
 */
const initAnchorNavigation = (smoother, snap) => {
  if (!smoother) return;

  document.addEventListener('click', (e) => {
    const link = e.target.closest('a[href^="#"]');
    if (!link) return;

    const hash = link.getAttribute('href');
    if (hash === '#') return;

    const target = document.querySelector(hash);
    if (!target) return;

    e.preventDefault();

    if (snap?.navigateTo) {
      snap.navigateTo(target);
    } else {
      // Mobile : pas de snap, scroll direct avec offset correct
      gsap.to(smoother, {
        scrollTop: smoother.offset(target, 'top top'),
        duration: 0.9,
        ease: 'power2.inOut',
      });
    }
  });
};

document.addEventListener('DOMContentLoaded', () => {
  initHeroBlobAnimation();
  const { closeMenu } = initHeroMenuToggle() ?? {};
  const heroTl = initHeroEntrance();
  const smoother = initScrollSmoother();
  initHeroMask();
  const snap = initScrollSnap(smoother, heroTl, closeMenu);
  initAnchorNavigation(smoother, snap);
  initTitleAnimations();
  initKeyBenefits();
  initSolutionOverview();
  initConcreteBenefits();
  initPlatformShowcase();
  initPricingModel();
  initPartnersShowcase();
  initFaq();
  ScrollTrigger.refresh();
});

window.addEventListener('load', () => {
  ScrollTrigger.refresh();
});
